import { useMemo, useState } from "react";

import {
  COLORS,
  aggregateAlignmentLagRows,
  aggregateBookingSourceRows,
  aggregateMonthlyRows,
  aggregateNeuroAppointmentRows,
  summarizeNeuroAppointmentWeeks,
} from "./enrollmentShared";
import type {
  EnrollmentBookingSourceRow,
  EnrollmentClinicOption,
  EnrollmentNeuroAppointmentRow,
  EnrollmentReferralRow,
} from "./enrollmentTypes";
import {
  AlignmentLagNormalizedChart,
  BookingSourcesChart,
  ClinicMultiSelect,
  ErrorState,
  LoadingState,
  MonthlyStackedChart,
  NeuroAppointmentsChart,
  NeuroAppointmentsKpi,
  NeuroClinicMultiSelect,
  SectionDivider,
} from "./enrollmentUi";

export function EnrollmentReferralsTab(props: {
  bookingError: string;
  bookingLoading: boolean;
  bookingRows: EnrollmentBookingSourceRow[];
  bookingClinics: EnrollmentClinicOption[];
  neuroAnchorDate: string;
  neuroClinics: EnrollmentClinicOption[];
  neuroError: string;
  neuroLoading: boolean;
  neuroRows: EnrollmentNeuroAppointmentRow[];
  recentDates: string[];
  referralClinics: string[];
  referralError: string;
  referralLoading: boolean;
  referralRows: EnrollmentReferralRow[];
  selectedDate: string;
  setSelectedDate: (date: string) => void;
}) {
  const [selectedClinics, setSelectedClinics] = useState<string[] | null>(null);
  const [selectedNeuroClinicIds, setSelectedNeuroClinicIds] = useState<string[] | null>(null);
  const [selectedBookingClinicIds, setSelectedBookingClinicIds] = useState<string[] | null>(null);

  const effectiveSelectedClinics = useMemo(() => {
    if (!props.referralClinics.length) return [];
    if (selectedClinics == null) return props.referralClinics;
    return props.referralClinics.filter((clinic) => selectedClinics.includes(clinic));
  }, [props.referralClinics, selectedClinics]);

  const effectiveNeuroIds = useMemo(() => {
    if (!props.neuroClinics.length) return [];
    if (selectedNeuroClinicIds == null) return props.neuroClinics.map((clinic) => clinic.id);
    const available = new Set(props.neuroClinics.map((clinic) => clinic.id));
    return selectedNeuroClinicIds.filter((id) => available.has(id));
  }, [props.neuroClinics, selectedNeuroClinicIds]);

  const effectiveBookingIds = useMemo(() => {
    if (!props.bookingClinics.length) return [];
    if (selectedBookingClinicIds == null) return props.bookingClinics.map((clinic) => clinic.id);
    const available = new Set(props.bookingClinics.map((clinic) => clinic.id));
    return selectedBookingClinicIds.filter((id) => available.has(id));
  }, [props.bookingClinics, selectedBookingClinicIds]);

  const referralRows = aggregateMonthlyRows(
    props.referralRows,
    effectiveSelectedClinics,
    "launchReferrals",
    "refreshReferrals",
  );
  const firstAlignedRows = aggregateMonthlyRows(
    props.referralRows,
    effectiveSelectedClinics,
    "initialCohortAligned",
    "referralRefreshAligned",
  );
  const alignmentLagRows = aggregateAlignmentLagRows(props.referralRows, effectiveSelectedClinics);
  const neuroAppointmentRows = aggregateNeuroAppointmentRows(
    props.neuroRows,
    effectiveNeuroIds,
    props.neuroAnchorDate,
  );
  const neuroAppointmentWeekSummary = summarizeNeuroAppointmentWeeks(
    props.neuroRows,
    effectiveNeuroIds,
    props.neuroAnchorDate,
  );
  const nonCancelledBookingSourceRows = aggregateBookingSourceRows(
    props.bookingRows,
    effectiveBookingIds,
    props.neuroAnchorDate,
    false,
  );
  const cancelledBookingSourceRows = aggregateBookingSourceRows(
    props.bookingRows,
    effectiveBookingIds,
    props.neuroAnchorDate,
    true,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-foreground">Analysis date:</span>
        <div className="flex flex-wrap gap-1.5">
          {props.recentDates.map((date) => (
            <button
              key={date}
              type="button"
              onClick={() => props.setSelectedDate(date)}
              className={
                props.selectedDate === date
                  ? "rounded-md border-2 border-foreground bg-muted px-2.5 py-1 text-xs font-semibold"
                  : "rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground"
              }
            >
              {date}
            </button>
          ))}
        </div>
      </div>

      <SectionDivider label="Enrollment calls by scheduling source" />
      {props.bookingLoading ? (
        <LoadingState />
      ) : props.bookingError ? (
        <ErrorState message={props.bookingError} />
      ) : (
        <>
          <NeuroClinicMultiSelect
            clinics={props.bookingClinics}
            selectedClinicIds={effectiveBookingIds}
            onChange={setSelectedBookingClinicIds}
            label="Clinic name"
            emptyMessage="No Enrollment or Referral bookings in this window."
          />
          <section className="rounded-lg border border-border/70 bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Scheduling source:</strong> Uses the same
            definitions as EOD scheduled enrollment calls. WS markers in the event title or Cal.com
            event-type title count as web-scheduled. Otherwise, the saved scheduling source
            identifies AI- or ES-scheduled calls; remaining bookings are unclassified. Inactive
            bookings are excluded. Clinic names come from linked Carespaces, using the earliest
            clinic link when there are several. Bookings without a named clinic link use the Cal.com
            event title; bookings without either appear under Unknown clinic.
          </section>
          <BookingSourcesChart title="Non-cancelled calls" rows={nonCancelledBookingSourceRows} />
          <BookingSourcesChart title="Cancelled calls" rows={cancelledBookingSourceRows} />
        </>
      )}

      <SectionDivider label="Upcoming neuro appointments — New-status carespaces" />
      {props.neuroLoading ? (
        <LoadingState />
      ) : props.neuroError ? (
        <ErrorState message={props.neuroError} />
      ) : (
        <>
          <NeuroClinicMultiSelect
            clinics={props.neuroClinics}
            selectedClinicIds={effectiveNeuroIds}
            onChange={setSelectedNeuroClinicIds}
          />
          <NeuroAppointmentsKpi summary={neuroAppointmentWeekSummary} />
          <NeuroAppointmentsChart rows={neuroAppointmentRows} anchorDate={props.neuroAnchorDate} />
        </>
      )}

      <SectionDivider label="Referrals" />
      {props.referralLoading ? (
        <LoadingState />
      ) : props.referralError ? (
        <ErrorState message={props.referralError} />
      ) : (
        <>
          <ClinicMultiSelect
            clinics={props.referralClinics}
            selectedClinics={effectiveSelectedClinics}
            onChange={setSelectedClinics}
          />
          <section className="rounded-lg border border-border/70 bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Launch date:</strong> The clinic’s first ever
            recorded Retell outbound call, or its first CareSpace creation date if it has no
            recorded outbound calls. Referrals before launch or within 30 days after launch belong
            to the launch cohort; later referrals are refresh referrals.
            <p className="mt-2">
              <strong className="text-foreground">Referral date:</strong> The earliest of CareSpace
              creation, the linked CRM referral source date, and the matched Espo source date. All
              dates use Eastern Time.
            </p>
          </section>
          <MonthlyStackedChart
            title="Referrals by month"
            subtitle="Monthly referrals split into launch and refresh cohorts using the launch date defined above."
            rows={referralRows}
            primaryLabel="Launch referrals"
            secondaryLabel="Refresh referrals"
            primaryColor={COLORS.blue}
            secondaryColor={COLORS.orange}
          />
          <MonthlyStackedChart
            title="First aligned by month"
            subtitle="CareSpaces first aligned in each month, split into the same launch cohort (initial cohort) and referral refresh."
            rows={firstAlignedRows}
            primaryLabel="Initial cohort"
            secondaryLabel="Referral refresh"
            primaryColor={COLORS.green}
            secondaryColor={COLORS.orange}
          />
          <AlignmentLagNormalizedChart rows={alignmentLagRows} />
        </>
      )}
    </div>
  );
}
